'use client';

import React, { forwardRef, useState, useEffect } from 'react';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { ExclamationCircleIcon } from '@heroicons/react/24/solid';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label: string;
  type?: 'email' | 'password' | 'text' | 'simple-password' | 'name' | 'phone' | 'number';
  error?: string;
  className?: string;
  labelClassName?: string;
  showPasswordValidation?: boolean;
  minLength?: number;
}

const passwordRequirements = [
  {
    id: 'uppercase',
    text: 'Please enter a capital letter',
    validator: (value: string) => /[A-Z]/.test(value)
  },
  {
    id: 'lowercase',
    text: 'Please enter a lowercase letter',
    validator: (value: string) => /[a-z]/.test(value)
  },
  {
    id: 'number',
    text: 'Please enter a number',
    validator: (value: string) => /\d/.test(value)
  },
  {
    id: 'special',
    text: 'Please enter a special character',
    validator: (value: string) => /[!@#$%^&*(),.?":{}|<>]/.test(value)
  },
  {
    id: 'length',
    text: 'Password must be at least 8 characters',
    validator: (value: string) => value.length >= 8
  }
];

// Standard max length limits for different field types
const getMaxLength = (type: string): number => {
  switch (type) {
    case 'email': return 255; // Standard email length limit
    case 'password':
    case 'simple-password': return 128; // Secure but reasonable limit
    case 'name': return 255; // Standard name length limit
    case 'phone': return 20; // International phone numbers
    case 'number': return 10; // General numeric fields
    case 'text':
    default: return 255; // General text fields
  }
};

// Standard min length limits for different field types
const getMinLength = (type: string): number => {
  switch (type) {
    case 'name': return 2; // Names should be at least 2 characters
    case 'password':
    case 'simple-password': return 8; // Password minimum
    case 'email': return 5; // Minimum valid email like a@b.c
    case 'text': return 2; // General text minimum
    case 'phone': return 10; // Minimum phone number
    case 'number': return 1; // Numbers can be single digit
    default: return 1; // Default minimum
  }
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, type = 'text', error, className = '', labelClassName = '', showPasswordValidation = false, value, onChange, maxLength, minLength, defaultValue, ...props }, ref) => {
    const [showPassword, setShowPassword] = useState(false);
    const [isFocused, setIsFocused] = useState(false);
    const [internalValue, setInternalValue] = useState(defaultValue as string || '');
    const [currentValidationMessage, setCurrentValidationMessage] = useState<string | null>(null);
    const [hasInteracted, setHasInteracted] = useState(false);

    // Use provided maxLength/minLength or default based on type
    const finalMaxLength = maxLength || getMaxLength(type);
    const finalMinLength = minLength || getMinLength(type);
    
    const inputType = (type === 'password' || type === 'simple-password') && showPassword ? 'text' : 
                     (type === 'simple-password' ? 'password' : 
                     (type === 'name' ? 'text' : 
                     (type === 'phone' ? 'tel' : type)));
    const isPassword = type === 'password';
    const isSimplePassword = type === 'simple-password';

    // Update internal value when prop value changes
    useEffect(() => {
      if (value !== undefined) {
        setInternalValue(value as string);
      }
    }, [value]);

    // Check validation only after user interaction
    useEffect(() => {
      if (isSimplePassword && hasInteracted) {
        const currentValue = value !== undefined ? value as string : internalValue;
        setCurrentValidationMessage(!currentValue ? 'Password cannot be empty.' : null);
      } else if (isPassword && showPasswordValidation && hasInteracted) {
        const currentValue = value !== undefined ? value as string : internalValue;
        updateValidationMessage(currentValue);
      }
    }, [isPassword, isSimplePassword, showPasswordValidation, value, internalValue, hasInteracted]);

    const updateValidationMessage = (val: string) => {
      if (!val) {
        setCurrentValidationMessage('Password cannot be empty.');
        return;
      }

      for (const req of passwordRequirements) {
        if (!req.validator(val)) {
          setCurrentValidationMessage(req.text);
          return;
        }
      }
      
      // All requirements met
      setCurrentValidationMessage(null);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value;

      // Enforce max length limit
      if (newValue.length > finalMaxLength) {
        return; // Don't update if exceeds max length
      }

      setInternalValue(newValue);
      // mark that user has interacted
      setHasInteracted(true);

      // Immediately check minimum length for validation message (don't block typing)
      if (newValue.length > 0 && newValue.length < finalMinLength && !isPassword && !isSimplePassword) {
        setCurrentValidationMessage(`${label} must be at least ${finalMinLength} characters`);
      } else if (!isPassword && !isSimplePassword) {
        setCurrentValidationMessage(null);
      }

      if (isPassword && showPasswordValidation) {
        updateValidationMessage(newValue);
      } else if (isSimplePassword) {
        setCurrentValidationMessage(!newValue ? 'Password cannot be empty.' : null);
      }

      if (onChange) {
        onChange(e);
      }
    };

    return (
      <div className="w-full font-work-sans font-normal">
        <label className={`block text-sm font-medium text-gray-800 mb-2 ${labelClassName}`}>
          {label}
        </label>
        <div className="relative">
          <input
            ref={ref}
            type={inputType}
            value={value !== undefined ? value : internalValue}
            onChange={handleInputChange}
            maxLength={finalMaxLength}
            className={`
              w-full px-4 2 border border-gray-300 rounded-lg
              focus:ring-2 focus:ring-blue-500 focus:border-transparent
              transition-all duration-200 outline-none py-2
              ${error || currentValidationMessage ? 'border-red-500 focus:ring-red-500 pr-10' : ''}
              ${className}
            `}
            onFocus={(e) => {
              setIsFocused(true);
              if (isPassword && showPasswordValidation) {
                updateValidationMessage(e.target.value);
              } else if (isSimplePassword) {
                setCurrentValidationMessage(!e.target.value ? 'Password cannot be empty.' : null);
              }
            }}
            onBlur={() => setIsFocused(false)}
            {...props}
          />
          {(error || currentValidationMessage) && (
            <ExclamationCircleIcon
              className="h-5 w-5 text-red-500 absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none"
            />
          )}
          {(isPassword || isSimplePassword) && (
            <button
              type="button"
              className={`absolute ${error || currentValidationMessage ? 'right-10' : 'right-3'} top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors`}
              onClick={() => setShowPassword(!showPassword)}
              tabIndex={-1}
            >
              {showPassword ? (
                <EyeSlashIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )}
            </button>
          )}
        </div>
        {error && (
          <p className="mt-1 text-sm text-red-600">{error}</p>
        )}
        {!error && currentValidationMessage && (
          <p className="mt-1 text-sm text-red-600">{currentValidationMessage}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;